import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { AuthenticationService } from '../authentication/authentication.service';
import { RoleGroup } from '../roles';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AdminModeService {
  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly logger = inject(NGXLogger);

  /**
   * If the user sees the controls to enable admin mode.
   *
   * This is false in AWP, because the UI header is not shown there.
   *
   * This is independent of the user permissions.
   */
  readonly adminModeReachable = signal(false);

  readonly adminModeToggle = signal(false);

  readonly hasAdminReadPermissions = computed(() => this.userService.userIsAllowed(RoleGroup.CDF_ADMIN));
  readonly hasAdminWritePermissions = computed(() => this.userService.userIsAllowed(RoleGroup.CDF_ADMIN_WRITE));

  /**
   * Which dpNr the current admin wants to use for all CDF functionality
   */
  readonly selectedDpNr: WritableSignal<string | undefined> = signal(undefined);

  readonly isAdminModeActive = computed(() => this.adminModeToggle() && !!this.selectedDpNr());

  /**
   * The dpNr that is chosen in admin mode, or undefined if admin mode is not active.
   */
  readonly activeAdminDpNr = computed(() => (this.isAdminModeActive() ? this.selectedDpNr() : undefined));

  private readonly updateAuthServiceAdminDpNr = effect(() => {
    this.authService.adminDistributionPartnerNumber.set(this.activeAdminDpNr());
  });

  /**
   * The country of the outlet that is chosen in admin mode, or undefined if admin mode is not active.
   */
  readonly activeAdminCountry = computed(() => {
    const dpNr = this.activeAdminDpNr();
    if (!dpNr) return undefined;

    const legalEntities = this.userService.legalEntitiesForAdminMode();
    for (const [country, entities] of legalEntities.entries()) {
      if (entities.some((entity) => entity.dpNr === dpNr)) {
        return country;
      }
    }
    this.logger.error('#CDFTF0035 No country found for admin dpNr', dpNr);
    return undefined;
  });

  /**
   * True if the user has activated admin mode, or the user’s outlet is whitelisted.
   *
   * CDF is unavailable if the user’s outlet is not whitelisted.
   * However, admin users can get around this by activating admin mode.
   */
  readonly whitelistedOrInAdminMode = computed(
    () => this.adminModeToggle() || this.userService.userOutletIsWhitelisted(),
  );
}
